import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent } from '@theme/dialog';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Renders a "quick view" button on the product card image overlay.
 * On click, fetches the product page and morphs its content into the
 * shared quick-add dialog (#quick-add-dialog) so shoppers can preview
 * the product without leaving the collection page.
 */
export class QuickViewComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  get productPageUrl() {
    return this.dataset.productUrl || '';
  }

  /**
   * Handles quick view button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const productPageUrl = this.productPageUrl;
    if (!productPageUrl) return;

    const html = await this.#fetchProductPage(productPageUrl);
    if (!html) return;

    const gridElement = html.querySelector('[data-product-grid-content]');
    if (!gridElement) return;

    const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));

    await this.#updateQuickViewModal(productGrid);
    this.#openQuickViewModal();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async #fetchProductPage(productPageUrl) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Morphs the fetched product markup into the shared modal content element.
   * @param {Element} productGrid - The product grid element
   */
  async #updateQuickViewModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);
  }

  #openQuickViewModal() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof DialogComponent)) return;

    dialogComponent.showDialog();
  }
}

if (!customElements.get('quick-view-component')) {
  customElements.define('quick-view-component', QuickViewComponent);
}
